import React from "react";
import { motion } from "framer-motion";
import { useGameState } from "../hooks/useGameState";
import { ArenaScreen } from "./ArenaScreen";
import { backdropVariants, modalVariants } from "../animations/variants";
import type { EggsFightingGameProps } from "../types";
import BackgroundImage from "../../../components/ui/BackgroundImage";
import bg from "../assets/images/bg.png";
import EggsFightingHeader from "./EggsFightingHeader";

/**
 * EggsFightingGame — mountable, self-contained Easter egg battle game.
 *
 * Accepts two players via props, manages the entire game lifecycle internally,
 * and calls onGameEnd when a winner is determined. Zero coupling to RageMP
 * or any external transport — callers handle all communication.
 *
 * player1 is always the local player ("YOUR EGG", right side).
 * player2 is always the opponent ("ENEMY EGG", left side).
 *
 * The game auto-starts on mount.
 */
export default function EggsFightingGame({
  titleWords = [{ text: "EASTER EGG" }, { text: "GAME", isHighlighted: true }],
  description = "test",
  player1,
  player2,
  onGameEnd,
  onClose,
  readyKey,
  onPlayerReady,
  readyCount,
  predefinedWinner,
}: EggsFightingGameProps): React.ReactElement {
  // onPlayerReady is NOT passed to useGameState — phase transitions are
  // driven by readyCount only. onPlayerReady is a UI concern handled in ArenaScreen.
  const { state } = useGameState(player1, player2, onGameEnd, readyCount, predefinedWinner);

  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      style={{ zIndex: 1000 }}
    >
      <BackgroundImage imageSrc={bg} imageClassName="!object-fill rounded-xl">
        <motion.div
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="relative flex flex-col overflow-hidden min-h-[550px] min-w-[800px] p-10"
          style={{ width: 810, maxWidth: "100vw", borderRadius: 10 }}
        >
          <EggsFightingHeader
            titleWords={titleWords}
            description={description}
            onClose={onClose}
            className="mb-8"
          />

          <ArenaScreen
            state={state}
            player1={player1}
            player2={player2}
            readyKey={readyKey}
            onPlayerReady={onPlayerReady}
            readyCount={readyCount}
          />
        </motion.div>
      </BackgroundImage>
    </motion.div>
  );
}
